import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getCompanyBranding } from '../services/companySettingsService';

/**
 * Add watermark to canvas
 * @param {HTMLCanvasElement} canvas - Canvas to add watermark to
 * @param {Object} branding - Company branding settings
 * @returns {HTMLCanvasElement} Canvas with watermark
 */
async function addWatermarkToCanvas(canvas, branding) {
  if (!branding?.watermarkEnabled || !branding?.logoUrl) {
    return canvas;
  }

  const ctx = canvas?.getContext('2d');
  const watermarkImg = new Image();
  watermarkImg.crossOrigin = 'anonymous';

  return new Promise((resolve, reject) => {
    watermarkImg.onload = () => {
      const canvasWidth = canvas?.width;
      const canvasHeight = canvas?.height;
      const watermarkWidth = canvasWidth * 0.3;
      const watermarkHeight = (watermarkImg?.height / watermarkImg?.width) * watermarkWidth;

      ctx.globalAlpha = branding?.watermarkOpacity || 0.15;

      let x, y;
      switch (branding?.watermarkPosition) {
        case 'center':
          x = (canvasWidth - watermarkWidth) / 2;
          y = (canvasHeight - watermarkHeight) / 2;
          break;
        case 'diagonal':
          ctx.save();
          ctx.translate(canvasWidth / 2, canvasHeight / 2);
          ctx.rotate(-Math.PI / 4);
          x = -watermarkWidth / 2;
          y = -watermarkHeight / 2;
          ctx.drawImage(watermarkImg, x, y, watermarkWidth, watermarkHeight);
          ctx.restore();
          ctx.globalAlpha = 1.0;
          resolve(canvas);
          return;
        case 'bottom-right':
          x = canvasWidth - watermarkWidth - 20;
          y = canvasHeight - watermarkHeight - 20;
          break;
        case 'top-left':
          x = 20;
          y = 20;
          break;
        default:
          x = (canvasWidth - watermarkWidth) / 2;
          y = (canvasHeight - watermarkHeight) / 2;
      }

      ctx.drawImage(watermarkImg, x, y, watermarkWidth, watermarkHeight);
      ctx.globalAlpha = 1.0;
      resolve(canvas);
    };

    watermarkImg.onerror = () => {
      console.warn('Failed to load watermark image');
      resolve(canvas);
    };

    watermarkImg.src = branding?.logoUrl;
  });
}

/**
 * Exports Arabic contract HTML to PDF with proper RTL formatting and optional watermark.
 * @param {string} contractHTML - HTML content of the contract.
 * @param {Object} metadata - Contract metadata for filename and properties.
 * @returns {Promise<void>} Downloads PDF file.
 */
export async function exportContractToPDF(contractHTML, metadata = {}) {
  try {
    // Get company branding settings
    let branding = null;
    try {
      branding = await getCompanyBranding();
    } catch (error) {
      console.warn('Could not load company branding:', error);
    }

    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = '210mm';
    tempContainer.style.padding = '20mm';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.fontFamily = 'Cairo, sans-serif';
    tempContainer.style.direction = 'rtl';
    tempContainer.style.fontSize = '12pt';
    tempContainer.style.lineHeight = '1.6';
    tempContainer.innerHTML = contractHTML;

    document.body?.appendChild(tempContainer);

    let canvas = await html2canvas(tempContainer, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    // Add watermark if enabled
    if (branding?.watermarkEnabled) {
      canvas = await addWatermarkToCanvas(canvas, branding);
    }

    document.body?.removeChild(tempContainer);

    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas?.height * imgWidth) / canvas?.width;
    let heightLeft = imgHeight;
    let position = 0;

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas?.toDataURL('image/png');

    pdf?.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf?.addPage();
      pdf?.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const filename = `عقد_${metadata?.contractType || 'عقاري'}_${metadata?.partyB || 'عميل'}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    pdf?.save(filename);

    return { success: true, filename };
  } catch (error) {
    console.error('Error exporting contract to PDF:', error);
    throw new Error('فشل في تصدير العقد إلى PDF. يرجى المحاولة مرة أخرى.');
  }
}

/**
 * Exports contract with custom page settings and optional watermark.
 * @param {string} contractHTML - HTML content.
 * @param {Object} options - Export options (orientation, format, quality).
 * @param {Object} metadata - Contract metadata.
 * @returns {Promise<void>} Downloads PDF file.
 */
export async function exportContractWithOptions(contractHTML, options = {}, metadata = {}) {
  try {
    // Get company branding settings
    let branding = null;
    try {
      branding = await getCompanyBranding();
    } catch (error) {
      console.warn('Could not load company branding:', error);
    }

    const {
      orientation = 'portrait',
      format = 'a4',
      quality = 2
    } = options;

    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = orientation === 'portrait' ? '210mm' : '297mm';
    tempContainer.style.padding = '20mm';
    tempContainer.style.backgroundColor = 'white';
    tempContainer.style.fontFamily = 'Cairo, sans-serif';
    tempContainer.style.direction = 'rtl';
    tempContainer.style.fontSize = '12pt';
    tempContainer.style.lineHeight = '1.6';
    tempContainer.innerHTML = contractHTML;

    document.body?.appendChild(tempContainer);

    let canvas = await html2canvas(tempContainer, {
      scale: quality,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    // Add watermark if enabled
    if (branding?.watermarkEnabled) {
      canvas = await addWatermarkToCanvas(canvas, branding);
    }

    document.body?.removeChild(tempContainer);

    const pdf = new jsPDF(orientation, 'mm', format);
    const imgData = canvas?.toDataURL('image/png');

    const pdfWidth = pdf?.internal?.pageSize?.getWidth();
    const pdfHeight = pdf?.internal?.pageSize?.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas?.height * pdfWidth) / canvas?.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf?.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf?.addPage();
      pdf?.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    const filename = `عقد_${metadata?.contractType || 'عقاري'}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    pdf?.save(filename);

    return { success: true, filename };
  } catch (error) {
    console.error('Error exporting contract with options:', error);
    throw new Error('فشل في تصدير العقد إلى PDF. يرجى المحاولة مرة أخرى.');
  }
}

export default {
  exportContractToPDF,
  exportContractWithOptions
};